import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { LanguageSelector } from '../components/LanguageSelector';
import { Loader2, Mail, ArrowLeft, CheckCircle, Copy, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setResetLink(null);
    setIsGoogleAccount(false);
    setEmailSent(false);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      const data = response.data;
      
      if (data.is_google_account) {
        setIsGoogleAccount(true);
        toast.info('This account uses Google Sign-In');
      } else if (data.email_sent) {
        setSuccess(true);
        setEmailSent(true);
        toast.success('Password reset email sent!');
      } else if (data.reset_link) {
        // Fallback if email failed
        setSuccess(true);
        setResetLink(window.location.origin + data.reset_link);
        toast.warning('Email unavailable - use the link below');
      } else {
        setSuccess(true);
        toast.success('If an account exists, you will receive an email.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(resetLink);
    toast.success('Link copied to clipboard!');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="forgot-password-page">
      {/* Background pattern */}
      <div className="absolute inset-0 hero-pattern opacity-30" />
      
      {/* Back button */}
      <Link 
        to="/login" 
        className="absolute top-4 left-4 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back to Login</span>
      </Link>

      {/* Language selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="ghost" />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700/50 bg-card/95 backdrop-blur">
        <CardHeader className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_egns-deutsch/artifacts/wljgvqe2_copy_DF94F79F-AD21-40BD-8A6F-78D3B8D2676A.jpeg" 
            alt="EGNS Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <CardTitle className="font-heading text-2xl">Reset Password</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a reset link
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!success && !isGoogleAccount ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700" 
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          ) : isGoogleAccount ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="h-8 w-8" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold">Google Account Detected</h3>
              <p className="text-muted-foreground">
                This account uses Google Sign-In. You cannot reset the password for Google accounts.
              </p>
              <Link to="/login">
                <Button className="w-full bg-teal-600 hover:bg-teal-700">
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : emailSent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Check Your Email!</h3>
              <p className="text-muted-foreground">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg text-left">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-200">Didn't receive the email?</p>
                    <ul className="mt-1 text-amber-700 dark:text-amber-300 list-disc list-inside">
                      <li>Check your spam/junk folder</li>
                      <li>Make sure you entered the correct email</li>
                      <li>Wait a few minutes and try again</li>
                    </ul>
                  </div>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setSuccess(false);
                  setEmailSent(false);
                }}
              >
                Try Another Email
              </Button>
            </div>
          ) : resetLink ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold">Email Service Unavailable</h3>
              <p className="text-sm text-muted-foreground">
                Use the link below to reset your password. This link expires in 1 hour.
              </p>
              
              <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
                <p className="text-xs font-mono break-all text-slate-600 dark:text-slate-300">
                  {resetLink}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={copyToClipboard}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
                <Link to={resetLink.replace(window.location.origin, '')} className="flex-1">
                  <Button className="w-full bg-teal-600 hover:bg-teal-700">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Reset Now
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Request Received</h3>
              <p className="text-muted-foreground">
                If an account with this email exists, you will receive a password reset link.
              </p>
              <Link to="/login">
                <Button className="w-full bg-teal-600 hover:bg-teal-700">
                  Back to Login
                </Button>
              </Link>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <div className="text-center text-sm">
            <span className="text-muted-foreground">Remember your password? </span>
            <Link to="/login" className="text-primary hover:underline font-medium">
              Login
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
